import type { NodePropertySchema } from '../../../types/node-properties';

/**
 * HTTP Request Node Schema
 * Based on n8n's HTTP Request V4.2 node
 * 
 * This is a comprehensive implementation with all major features:
 * - Multiple HTTP methods
 * - Authentication options
 * - Headers, query parameters, body
 * - Response handling options
 * - Pagination support
 */
const schema: NodePropertySchema = {
  nodeType: 'httpRequest',
  version: '4.2.0',
  
  properties: [
    // ==================================================================
    // AUTHENTICATION
    // ==================================================================
    {
      key: 'authentication',
      label: 'Authentication',
      type: 'options',
      options: [
        { name: 'None', value: 'none' },
        { name: 'Basic Auth', value: 'basicAuth' },
        { name: 'Header Auth', value: 'headerAuth' },
        { name: 'Query Auth', value: 'queryAuth' },
        { name: 'OAuth2', value: 'oauth2' }
      ],
      default: 'none',
      description: 'The authentication method to use'
    },

    // Credential selectors - filtered by auth type
    {
      key: 'credentialId',
      label: 'Credential',
      type: 'credential',
      default: '',
      description: 'Select a Basic Auth credential',
      displayOptions: {
        show: {
          authentication: ['basicAuth']
        }
      },
      credentialTypes: [{ name: 'http_basic' }, { name: 'basic_auth' }]
    },
    {
      key: 'credentialId',
      label: 'Credential',
      type: 'credential',
      default: '',
      description: 'Select a Header Auth credential (API Key or Bearer Token)',
      displayOptions: {
        show: {
          authentication: ['headerAuth']
        }
      },
      credentialTypes: [{ name: 'api_key_header' }, { name: 'bearer_token' }, { name: 'http_bearer' }, { name: 'api_key' }]
    },
    {
      key: 'credentialId',
      label: 'Credential',
      type: 'credential',
      default: '',
      description: 'Select a Query Auth credential',
      displayOptions: {
        show: {
          authentication: ['queryAuth']
        }
      },
      credentialTypes: [{ name: 'api_key' }]
    },
    {
      key: 'credentialId',
      label: 'Credential',
      type: 'credential',
      default: '',
      description: 'Select an OAuth2 credential',
      displayOptions: {
        show: {
          authentication: ['oauth2']
        }
      },
      credentialTypes: [{ name: 'oauth2' }]
    },
    
    // ==================================================================
    // HTTP METHOD
    // ==================================================================
    {
      key: 'method',
      label: 'Method',
      type: 'options',
      options: [
        { name: 'DELETE', value: 'DELETE' },
        { name: 'GET', value: 'GET' },
        { name: 'HEAD', value: 'HEAD' },
        { name: 'OPTIONS', value: 'OPTIONS' },
        { name: 'PATCH', value: 'PATCH' },
        { name: 'POST', value: 'POST' },
        { name: 'PUT', value: 'PUT' }
      ],
      default: 'GET',
      description: 'The HTTP method to use for the request'
    },
    
    // ==================================================================
    // URL
    // ==================================================================
    {
      key: 'url',
      label: 'URL',
      type: 'string',
      default: '',
      placeholder: 'https://api.example.com/endpoint',
      required: true,
      description: 'The URL to make the HTTP request to',
      validation: [
        {
          type: 'required',
          message: 'URL is required'
        },
        {
          type: 'url',
          message: 'Please enter a valid URL'
        }
      ]
    },
    
    // ==================================================================
    // QUERY PARAMETERS
    // ==================================================================
    {
      key: 'sendQuery',
      label: 'Query Parameters',
      type: 'boolean',
      default: false,
      description: 'Whether to send query parameters'
    },
    
    {
      key: 'queryParameters',
      label: 'Query Parameters',
      type: 'fixedCollection',
      typeOptions: {
        multipleValues: true
      },
      displayOptions: {
        show: {
          sendQuery: [true]
        }
      },
      default: {},
      placeholder: 'Add Parameter',
      fixedCollectionOptions: [
        {
          name: 'parameters',
          displayName: 'Parameter',
          values: [
            {
              key: 'name',
              label: 'Name',
              type: 'string',
              default: '',
              placeholder: 'param_name',
              required: true
            },
            {
              key: 'value',
              label: 'Value',
              type: 'string',
              default: '',
              placeholder: 'param_value'
            }
          ]
        }
      ]
    },
    
    // ==================================================================
    // HEADERS
    // ==================================================================
    {
      key: 'sendHeaders',
      label: 'Headers',
      type: 'boolean',
      default: false,
      description: 'Whether to send custom headers'
    },
    
    {
      key: 'headerParameters',
      label: 'Header Parameters',
      type: 'fixedCollection',
      typeOptions: {
        multipleValues: true
      },
      displayOptions: {
        show: {
          sendHeaders: [true]
        }
      },
      default: {},
      placeholder: 'Add Header',
      fixedCollectionOptions: [
        {
          name: 'parameters',
          displayName: 'Header',
          values: [
            {
              key: 'name',
              label: 'Name',
              type: 'string',
              default: '',
              placeholder: 'Content-Type',
              required: true
            },
            {
              key: 'value',
              label: 'Value',
              type: 'string',
              default: '',
              placeholder: 'application/json'
            }
          ]
        }
      ]
    },
    
    // ==================================================================
    // BODY
    // ==================================================================
    {
      key: 'sendBody',
      label: 'Send Body',
      type: 'boolean',
      default: false,
      description: 'Whether to send a body with the request',
      displayOptions: {
        show: {
          method: ['PATCH', 'POST', 'PUT', 'DELETE']
        }
      }
    },
    
    {
      key: 'bodyContentType',
      label: 'Body Content Type',
      type: 'options',
      displayOptions: {
        show: {
          sendBody: [true],
          method: ['PATCH', 'POST', 'PUT', 'DELETE']
        }
      },
      options: [
        {
          name: 'JSON',
          value: 'json',
          description: 'Send JSON data'
        },
        {
          name: 'Form-Data Multipart',
          value: 'multipart-form-data',
          description: 'Send multipart/form-data'
        },
        {
          name: 'Form Urlencoded',
          value: 'form-urlencoded',
          description: 'Send application/x-www-form-urlencoded'
        },
        {
          name: 'Raw/Custom',
          value: 'raw',
          description: 'Send raw data'
        },
        {
          name: 'n8n Binary File',
          value: 'binaryData',
          description: 'Send binary data from previous node'
        }
      ],
      default: 'json',
      description: 'Content type of the body to send'
    },
    
    {
      key: 'jsonBody',
      label: 'JSON',
      type: 'json',
      displayOptions: {
        show: {
          sendBody: [true],
          bodyContentType: ['json']
        }
      },
      default: '{}',
      typeOptions: {
        rows: 10
      },
      description: 'JSON body to send'
    },
    
    {
      key: 'bodyParameters',
      label: 'Body Parameters',
      type: 'fixedCollection',
      typeOptions: {
        multipleValues: true
      },
      displayOptions: {
        show: {
          sendBody: [true],
          bodyContentType: ['multipart-form-data', 'form-urlencoded']
        }
      },
      default: {},
      placeholder: 'Add Parameter',
      fixedCollectionOptions: [
        {
          name: 'parameters',
          displayName: 'Parameter',
          values: [
            {
              key: 'name',
              label: 'Name',
              type: 'string',
              default: '',
              required: true
            },
            {
              key: 'value',
              label: 'Value',
              type: 'string',
              default: ''
            }
          ]
        }
      ]
    },
    
    {
      key: 'rawBody',
      label: 'Body',
      type: 'string',
      displayOptions: {
        show: {
          sendBody: [true],
          bodyContentType: ['raw']
        }
      },
      default: '',
      typeOptions: {
        rows: 10
      },
      placeholder: 'Raw body content'
    },
    
    // ==================================================================
    // OPTIONS
    // ==================================================================
    {
      key: 'options',
      label: 'Options',
      type: 'collection',
      placeholder: 'Add Option',
      default: {},
      values: [
        {
          key: 'followRedirect',
          label: 'Follow Redirects',
          type: 'boolean',
          default: true,
          description: 'Whether to follow HTTP 3xx redirects'
        },
        {
          key: 'ignoreResponseCode',
          label: 'Ignore Response Code',
          type: 'boolean',
          default: false,
          description: 'Whether to succeed even if response code is not 2xx'
        },
        {
          key: 'proxy',
          label: 'Proxy',
          type: 'string',
          default: '',
          placeholder: 'http://proxy.example.com:8080',
          description: 'HTTP proxy to use for the request'
        },
        {
          key: 'timeout',
          label: 'Timeout',
          type: 'number',
          default: 10000,
          description: 'Time in ms to wait for the server to respond before aborting',
          typeOptions: {
            minValue: 0,
            maxValue: 300000
          }
        },
        {
          key: 'allowUnauthorizedCerts',
          label: 'Allow Unauthorized Certificates',
          type: 'boolean',
          default: false,
          description: 'Whether to allow self-signed or invalid SSL certificates'
        },
        {
          key: 'responseFormat',
          label: 'Response Format',
          type: 'options',
          options: [
            { name: 'Autodetect', value: 'autodetect' },
            { name: 'JSON', value: 'json' },
            { name: 'Text', value: 'text' },
            { name: 'File', value: 'file' }
          ],
          default: 'autodetect',
          description: 'How to parse the response'
        },
        {
          key: 'fullResponse',
          label: 'Include Response Headers and Status',
          type: 'boolean',
          default: false,
          description: 'Whether to return full response (headers, status code) or just body'
        }
      ]
    },
    
    // ==================================================================
    // PAGINATION (Advanced)
    // ==================================================================
    {
      key: 'pagination',
      label: 'Pagination',
      type: 'fixedCollection',
      typeOptions: {
        multipleValues: false
      },
      default: {},
      placeholder: 'Add Pagination',
      description: 'Automatically paginate through results',
      fixedCollectionOptions: [
        {
          name: 'pagination',
          displayName: 'Pagination Settings',
          values: [
            {
              key: 'paginationMode',
              label: 'Pagination Mode',
              type: 'options',
              options: [
                { name: 'Off', value: 'off' },
                { name: 'Update a Parameter in Each Request', value: 'updateAParameter' },
                { name: 'Response Contains Next URL', value: 'responseContainsNextURL' }
              ],
              default: 'off',
              description: 'How to paginate'
            },
            // ---- Fields for "Update a Parameter in Each Request" mode ----
            {
              key: 'paginationType',
              label: 'Pagination Type',
              type: 'options',
              options: [
                { name: 'Offset/Limit', value: 'offset' },
                { name: 'Page Number', value: 'page' }
              ],
              default: 'offset',
              displayOptions: {
                show: {
                  paginationMode: ['updateAParameter']
                }
              },
              description: 'The type of pagination to use'
            },
            // Offset/Limit specific fields
            {
              key: 'paginationLimit',
              label: 'Limit (Page Size)',
              type: 'number',
              default: 100,
              typeOptions: {
                minValue: 1,
                maxValue: 10000
              },
              displayOptions: {
                show: {
                  paginationMode: ['updateAParameter'],
                  paginationType: ['offset']
                }
              },
              description: 'Number of items to fetch per page'
            },
            {
              key: 'paginationLimitParam',
              label: 'Limit Parameter Name',
              type: 'string',
              default: 'limit',
              displayOptions: {
                show: {
                  paginationMode: ['updateAParameter'],
                  paginationType: ['offset']
                }
              },
              description: 'The query parameter name for the limit (e.g., "limit", "pageSize", "per_page")'
            },
            {
              key: 'paginationOffsetParam',
              label: 'Offset Parameter Name',
              type: 'string',
              default: 'offset',
              displayOptions: {
                show: {
                  paginationMode: ['updateAParameter'],
                  paginationType: ['offset']
                }
              },
              description: 'The query parameter name for the offset (e.g., "offset", "skip", "start")'
            },
            // Page number specific fields
            {
              key: 'paginationPageParam',
              label: 'Page Parameter Name',
              type: 'string',
              default: 'page',
              displayOptions: {
                show: {
                  paginationMode: ['updateAParameter'],
                  paginationType: ['page']
                }
              },
              description: 'The query parameter name for the page number (e.g., "page", "pageNumber", "p")'
            },
            // ---- Fields for "Response Contains Next URL" mode ----
            {
              key: 'paginationCursorPath',
              label: 'Next Cursor Path',
              type: 'string',
              default: 'nextCursor',
              displayOptions: {
                show: {
                  paginationMode: ['responseContainsNextURL']
                }
              },
              description: 'JSON path to the next cursor/URL in the response (e.g., "data.nextCursor", "pagination.next", "meta.next_page_url")'
            },
            {
              key: 'paginationCursorParam',
              label: 'Cursor Parameter Name',
              type: 'string',
              default: 'cursor',
              displayOptions: {
                show: {
                  paginationMode: ['responseContainsNextURL']
                }
              },
              description: 'The query parameter name to send the cursor value (e.g., "cursor", "after", "next_token")'
            },
            // ---- Common fields for both pagination modes ----
            {
              key: 'maxPages',
              label: 'Maximum Pages',
              type: 'number',
              displayOptions: {
                hide: {
                  paginationMode: ['off']
                }
              },
              default: 10,
              typeOptions: {
                minValue: 1,
                maxValue: 1000
              },
              description: 'Maximum number of pages to fetch'
            },
            {
              key: 'paginationCompleteWhen',
              label: 'Stop When Response Contains',
              type: 'string',
              default: '',
              displayOptions: {
                hide: {
                  paginationMode: ['off']
                }
              },
              description: 'Stop pagination when the response contains this string (optional)'
            }
          ]
        }
      ]
    }
  ],
  
  metadata: {
    author: 'Evo E2E Team',
    documentation: 'https://docs.n8n.io/integrations/builtin/core-nodes/n8n-nodes-base.httprequest/'
  }
};

export default schema;
